import React, { useState, useEffect, useMemo } from 'react';
import { Maximize, Minimize, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface NumberLineComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const NumberLineComponent: React.FC<NumberLineComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  
  // Support both legacy structure and new config structure
  const getConfigValue = (key: string, defaultValue: any) => {
    // Check if resource has a config object (new structure)
    if (resource.config && typeof resource.config === 'object') {
      return resource.config[key] !== undefined ? resource.config[key] : defaultValue;
    }
    // Fall back to legacy structure (direct properties on resource)
    return resource[key] !== undefined ? resource[key] : defaultValue;
  };

  const min = getConfigValue('min', 0);
  const max = getConfigValue('max', 20);
  const step = getConfigValue('step', 1);
  const tickEvery = getConfigValue('tick_every', 1);
  const showLabels = getConfigValue('showLabels', true);
  const showTicks = getConfigValue('showTicks', true);
  const theme = getConfigValue('theme', 'primary');
  const interactive = getConfigValue('interactive', true);

  const [currentValue, setCurrentValue] = useState(min);
  const [highlighted, setHighlighted] = useState<number[]>([]);
  const [focused, setFocused] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [scrollPosition, setScrollPosition] = useState(0);

  const range = max - min + 1;

  // Get theme colors
  const getThemeColors = () => {
    switch (theme) {
      case 'blue':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          button: 'border-blue-300 hover:border-blue-400',
          focused: 'border-blue-500 bg-blue-50 text-blue-700',
          highlighted: 'border-blue-400 bg-blue-50 text-blue-700'
        };
      case 'green':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-900',
          button: 'border-green-300 hover:border-green-400',
          focused: 'border-green-500 bg-green-50 text-green-700',
          highlighted: 'border-green-400 bg-green-50 text-green-700'
        };
      case 'purple':
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          text: 'text-purple-900',
          button: 'border-purple-300 hover:border-purple-400',
          focused: 'border-purple-500 bg-purple-50 text-purple-700',
          highlighted: 'border-purple-400 bg-purple-50 text-purple-700'
        };
      case 'secondary':
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-900',
          button: 'border-gray-300 hover:border-gray-400',
          focused: 'border-gray-500 bg-gray-50 text-gray-700',
          highlighted: 'border-gray-400 bg-gray-50 text-gray-700'
        };
      default: // primary
        return {
          bg: 'bg-white',
          border: 'border-gray-200',
          text: 'text-gray-900',
          button: 'border-gray-300 hover:border-gray-400',
          focused: 'border-blue-500 bg-blue-50 text-blue-700',
          highlighted: 'border-yellow-400 bg-yellow-50 text-yellow-700'
        };
    }
  };

  const themeColors = getThemeColors();

  // Reset component state when resource changes (for quiz navigation)
  useEffect(() => {
    setCurrentValue(min);
    setHighlighted([]);
    setFocused(null);
    setZoomLevel(1);
    setScrollPosition(0);
  }, [resource.id, min, max]);

  const handleNumberClick = (num: number) => {
    if (!interactive) return;
    setCurrentValue(num);
    setFocused(num);
    onAction?.('number_clicked', { number: num });
  };

  const handleHighlight = (start: number, end: number) => {
    if (!interactive) return;
    setHighlighted(Array.from({ length: end - start + 1 }, (_, i) => start + i));
    onAction?.('highlight_range', { start, end });
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleScroll = (direction: 'left' | 'right') => {
    const scrollAmount = 100;
    setScrollPosition(prev => {
      if (direction === 'left') {
        return Math.max(0, prev - scrollAmount);
      } else {
        return Math.min(maxScroll, prev + scrollAmount);
      }
    });
  };

  // Calculate responsive layout
  const layout = useMemo(() => {
    const isLargeRange = range > 15;
    const isVeryLargeRange = range > 30;
    const isExtremelyLargeRange = range > 50;
    
    if (isExtremelyLargeRange) {
      // For extremely large ranges, show every 10th number with very small buttons
      return {
        step: step || tickEvery,
        buttonSize: 'w-5 h-5 text-xs',
        showAll: false
      };
    } else if (isVeryLargeRange) {
      // For very large ranges, show every 5th number with smaller buttons
      return {
        step: step || tickEvery,
        buttonSize: 'w-6 h-6 text-xs',
        showAll: false
      };
    } else if (isLargeRange) {
      // For large ranges, show every 2nd number
      return {
        step: step || tickEvery,
        buttonSize: 'w-7 h-7 text-sm',
        showAll: false
      };
    } else {
      // For smaller ranges, show all numbers
      return {
        step: step || tickEvery,
        buttonSize: 'w-8 h-8 text-sm',
        showAll: false
      };
    }
  }, [range, step, tickEvery]);

  const numbers = useMemo(() => {
    return Array.from({ length: Math.ceil(range / layout.step) }, (_, i) => min + (i * layout.step));
  }, [min, range, layout]);

  // Calculate max scroll based on button size and container width
  const getButtonWidth = () => {
    switch (layout.buttonSize) {
      case 'w-5 h-5': return 20; // 5 * 4 = 20px
      case 'w-6 h-6': return 24; // 6 * 4 = 24px
      case 'w-7 h-7': return 28; // 7 * 4 = 28px
      case 'w-8 h-8': return 32; // 8 * 4 = 32px
      default: return 32;
    }
  };
  
  const buttonWidth = getButtonWidth();
  const spacing = 4; // space-x-1 = 4px
  const totalWidth = numbers.length * (buttonWidth + spacing);
  const containerWidth = 400; // Approximate container width
  const maxScroll = Math.max(0, totalWidth - containerWidth);

  return (
    <div className={`${themeColors.bg} rounded-lg border ${themeColors.border} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-semibold ${themeColors.text}`}>{t('numberLine.numberLine')}</h3>
        <div className="flex items-center space-x-2">
          {/* Zoom controls */}
          <button
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.5}
            className="p-1 text-gray-600 hover:bg-gray-50 rounded disabled:opacity-50"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={handleZoomIn}
            disabled={zoomLevel >= 3}
            className="p-1 text-gray-600 hover:bg-gray-50 rounded disabled:opacity-50"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          {/* Highlight controls */}
          <button
            onClick={() => handleHighlight(min, max)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
            title="Highlight All"
          >
            <Maximize className="w-4 h-4" />
          </button>
          <button
            onClick={() => setHighlighted([])}
            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
            title="Clear Highlight"
          >
            <Minimize className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Number line container with scroll controls */}
      <div className="relative">
        {/* Scroll left button */}
        {scrollPosition > 0 && (
          <button
            onClick={() => handleScroll('left')}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-1 shadow-md hover:bg-gray-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        
        {/* Scroll right button */}
        {scrollPosition < maxScroll && (
          <button
            onClick={() => handleScroll('right')}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-1 shadow-md hover:bg-gray-50"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
        
        {/* Number line */}
        <div 
          className="flex items-center space-x-1 overflow-x-auto pb-2 scrollbar-hide"
          style={{ 
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'left center',
            scrollBehavior: 'smooth'
          }}
        >
          <div 
            className="flex items-center space-x-1"
            style={{ transform: `translateX(-${scrollPosition}px)` }}
          >
            {numbers.map((num) => (
              <div key={num} className="flex flex-col items-center">
                {showLabels && (
                  <button
                    onClick={() => handleNumberClick(num)}
                    className={`flex-shrink-0 ${layout.buttonSize} rounded-lg border-2 transition-all ${
                      focused === num
                        ? themeColors.focused
                        : highlighted.includes(num)
                        ? themeColors.highlighted
                        : themeColors.button
                    }`}
                  >
                    {num}
                  </button>
                )}
                {showTicks && (
                  <div className={`w-1 h-4 ${theme === 'primary' ? 'bg-gray-300' : 'bg-gray-400'}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Range indicator */}
      <div className={`mt-3 text-sm ${themeColors.text.replace('900', '600')}`}>
        {t('numberLine.current')}: {currentValue} | {t('numberLine.range')}: {min} - {max}
        {!layout.showAll && (
          <span className={`ml-2 text-xs ${themeColors.text.replace('900', '500')}`}>
            ({t('numberLine.showingEvery')} {layout.step === 1 ? t('numberLine.number') : layout.step === 2 ? `2nd ${t('numberLine.numbers')}` : layout.step === 5 ? `5th ${t('numberLine.numbers')}` : `10th ${t('numberLine.numbers')}`})
          </span>
        )}
      </div>
      
      {/* Zoom indicator */}
      <div className={`mt-1 text-xs ${themeColors.text.replace('900', '500')}`}>
        {t('numberLine.zoom')}: {Math.round(zoomLevel * 100)}%
      </div>
    </div>
  );
};
